import * as React from 'react'
import { createPortal } from 'react-dom'
import { Check, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { isGroupedOptions, type SelectOption, type SelectOptions } from './select-option-utils'
import type { SelectDropdownPosition } from './use-select-dropdown-position'

/** Search box (when searchable) in the open list; it is the combobox while it has focus. */
export interface SelectPopoverSearchProps {
  inputRef: React.RefObject<HTMLInputElement | null>
  query: string
  placeholder: string
  /** Accessible name of the search box (e.g. "Search Salon"). */
  ariaLabel: string
  /** Names the box by reference instead (wins over `ariaLabel`), e.g. "Search" + an outside label. */
  labelledBy?: string
  onQueryChange: (query: string) => void
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
  activeDescendant?: string
}

interface SelectListboxPopoverProps {
  /** Outer popover box (search + list); used for outside-click detection. */
  popoverRef: React.RefObject<HTMLDivElement | null>
  listboxId: string
  /** Names the listbox: the visible label, or the trigger when there is none. */
  labelledBy: string
  position: SelectDropdownPosition
  options: SelectOptions
  value?: string
  activeValue: string | null
  optionId: (value: string) => string
  onActivate: (value: string) => void
  onPick: (value: string) => void
  search?: SelectPopoverSearchProps
}

/**
 * The open dropdown of `Select`, portalled to <body> so it escapes overflow clips.
 * One row style for every variant: inset rounded 44px rows, hover/keyboard highlight
 * in neutral grey, the selected row in medium weight with a trailing violet check
 * (no fill, so "highlighted" and "selected" stay distinguishable).
 */
export function SelectListboxPopover({
  popoverRef,
  listboxId,
  labelledBy,
  position,
  options,
  value,
  activeValue,
  optionId,
  onActivate,
  onPick,
  search,
}: SelectListboxPopoverProps) {
  const renderOption = (option: SelectOption) => {
    const isSelected = option.value === value
    const isActive = option.value === activeValue
    return (
      // A real <button> (role="option" is allowed on it) so disabled rows are natively unclickable;
      // tabIndex -1 because focus stays on the trigger / search box (aria-activedescendant).
      <button
        key={option.value}
        type="button"
        tabIndex={-1}
        id={optionId(option.value)}
        role="option"
        aria-selected={isSelected}
        disabled={option.disabled}
        aria-disabled={option.disabled || undefined}
        // Keep focus on the trigger / search box while clicking a row.
        onMouseDown={(event) => event.preventDefault()}
        onMouseMove={() => {
          if (!option.disabled && !isActive) onActivate(option.value)
        }}
        onClick={() => onPick(option.value)}
        className={cn(
          'flex w-full min-h-11 items-center justify-between gap-2.5 rounded-[var(--r-md)] px-3 py-2',
          // 16px: the app's minimum text size (`text-base` is 15px in this app).
          'text-left text-[16px] text-clovie-text transition-colors duration-100 focus:outline-none',
          option.disabled ? 'cursor-not-allowed text-clovie-muted' : 'cursor-pointer',
          isActive && !option.disabled && 'bg-clovie-hover',
          isSelected && 'font-medium'
        )}
      >
        <span className="min-w-0 truncate">{option.label}</span>
        {option.hint ? <span className="ml-auto shrink-0 font-normal text-clovie-muted">{option.hint}</span> : null}
        <Check
          aria-hidden="true"
          className={cn('h-[18px] w-[18px] shrink-0 text-clovie-brand', !isSelected && 'invisible')}
        />
      </button>
    )
  }

  const isEmpty = options.length === 0

  return createPortal(
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        left: position.left,
        width: position.width,
        minWidth: position.minWidth,
        maxWidth: position.maxWidth,
        top: position.top,
        bottom: position.bottom,
        maxHeight: position.maxHeight,
      }}
      className={cn(
        // Above every modal/drawer layer (they top out around z-[10002]) so a Select inside one shows.
        'z-[10050] flex flex-col overflow-hidden p-1.5',
        'rounded-[var(--r-xl)] border border-clovie-border bg-clovie-surface shadow-soft-lg'
      )}
    >
      {search && (
        <div className="relative mb-1.5 shrink-0">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-clovie-faint"
          />
          <input
            ref={search.inputRef}
            type="text"
            role="combobox"
            aria-label={search.ariaLabel}
            aria-labelledby={search.labelledBy}
            aria-expanded="true"
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={search.activeDescendant}
            autoComplete="off"
            data-field=""
            value={search.query}
            onChange={(event) => search.onQueryChange(event.target.value)}
            onKeyDown={search.onKeyDown}
            placeholder={search.placeholder}
            className={cn(
              'h-11 w-full pl-[38px] pr-3',
              'bg-clovie-surface-2 text-[16px] text-clovie-text',
              'border border-clovie-border rounded-[var(--r-md)]',
              'placeholder:text-clovie-muted focus:outline-none'
            )}
          />
        </div>
      )}

      <div
        role="listbox"
        id={listboxId}
        aria-labelledby={labelledBy}
        className="min-h-0 flex-1 overflow-auto"
      >
        {isGroupedOptions(options)
          ? options.map((group, groupIndex) => {
              const headerId = `${listboxId}-group-${groupIndex}`
              return (
                <div key={group.label} role="group" aria-labelledby={headerId}>
                  <div id={headerId} className="px-3 pb-1 pt-2.5 text-[16px] font-semibold text-clovie-muted">
                    {group.label}
                  </div>
                  {group.options.map(renderOption)}
                </div>
              )
            })
          : options.map(renderOption)}

        {isEmpty && <div className="px-3 py-3 text-center text-[16px] text-clovie-muted">No results found</div>}
      </div>
    </div>,
    document.body
  )
}
